import { useMutation } from '@apollo/client';
import * as React from 'react';
import styled from 'styled-components';

import { createLeadMutation } from '../../../../client/__graphql__/mutations';
import { useForm } from '../../../utils/hooks/useForm';
import { ILeadInput, ILeadResponse } from '../../../__types__/global';
import { Button } from '../button/Button';
import { FormRow } from './Form';
import { FormError } from './FormError';
import { Input } from './Input';
import { animated } from 'react-spring';
import { Paragraph } from '../text/Paragraph';
import { Line } from '../divider/Line';
import { ModalContext } from '../modal/modal';
import { Heading } from '../text/Heading';
import { useClickoutside } from '../../../utils/hooks/useClickOutside';

export const FormSectionNewsLetter: React.FC<{
  mailType: number;
}> = ({ mailType }) => {
  const [posted, setPosted] = React.useState(false);
  const wrapperRef = React.useRef<any>();
  const { showModal, hideModal } = React.useContext(ModalContext);
  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
              success
            }`,
    },
  });

  const [mail, { loading, error }] = useMutation<ILeadResponse, ILeadInput>(
    MAIL_MUTATION,
    {
      onCompleted: ({ lead }) => {
        if (lead.mail?.success) {
          setPosted(true);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  useClickoutside({
    ref: wrapperRef,
    onClickOutside: () => {
      hideModal();
    },
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Eks. Ola Nordmann',
        value: '',
        label: 'Fullt navn',
        labelAfter: '',
        required: true,
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post',
        labelAfter: '',
        required: true,
      },
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      let comment = ``;
      mail({
        variables: {
          input: {
            ...preparedFields,
            referer: window.location.href,
            leadType: 'EPOST',
            mailType: mailType,
            comment: comment,
            zip: '0000',
          },
        },
      });
    },
  });

  return (
    <>
      <NewsLetterStyle ref={wrapperRef}>
        <FormWrapper style={{ padding: '1em' }}>
          {/* Lukker modal ved klikk på kryss ikon */}
          <CloseIcon
            className="modal-close"
            onClick={() => {
              showModal({ component: null });
            }}
          />
          {!posted ? (
            <Form noValidate style={{ paddingBottom: '50px' }}>
              <h1>Nyhetsbrev</h1>
              <Line style={{ margin: '0 auto' }} />
              <Paragraph
                center={true}
                style={{ paddingTop: '1em', fontWeight: '100' }}
              >
                Hold deg oppdatert med bolig relatert informasjon via vårt
                nyhetsbrev.
              </Paragraph>

              {error &&
                error.graphQLErrors.map((error, index) => (
                  <FormError key={`error_${index}`} message={error.message} />
                ))}
              {fields.map((item, index) => {
                if (item) {
                  return (
                    <FormRow key={`field_${index}`}>
                      <Input
                        type={item.type}
                        name={item.name}
                        label={item.label}
                        labelAfter={item.labelAfter}
                        placeholder={item.placeholder}
                        value={item.value}
                        onChange={handleChange}
                        error={item.error}
                      />
                    </FormRow>
                  );
                }
              })}
              <Paragraph
                center={true}
                style={{ paddingBottom: '1em', fontWeight: '100' }}
              >
                Jeg anmoder PrivatMegleren om å kontakte meg på e-post og /
                eller telefoni form av sms eller oppringing.
              </Paragraph>
              <Button
                type="primary"
                onClick={handleSubmit}
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Kontakt meg
              </Button>
            </Form>
          ) : (
            <ConfirmMessage>
              <Heading tag="h2" center={true} className="h2">
                Du er nå påmeldt Privatmeglerens nyhetsbrev!
              </Heading>
            </ConfirmMessage>
          )}
        </FormWrapper>
      </NewsLetterStyle>
    </>
  );
};

const ConfirmMessage = styled.div`
  height: 200px;
  width: 100%;
  margin: 0 auto;
  .h2 {
    margin-top: 0em !important;
    padding-top: 2em;
  }
  @media all and (min-width: 315px) and (max-width: 450px) {
    .h2 {
      margin-top: -15px;
    }
  }
  @media all and (max-width: 315px) {
    .h2 {
      margin-top: -15px;
    }
  }
`;

const FormWrapper = styled(animated.div)`
  position: relative;
`;
const NewsLetterStyle = styled.div`
  width: 100%;
  max-width: 508px;
  background-color: ${({ theme }) => theme.colors.darkburgundy};
  position: fixed;
  margin-top: -1em;

  h1 {
    padding-top: 0.5em;
    color: ${({ theme }) => theme.colors.primary};
    text-align: center;
  }
  .modal-close {
    position: relative;
    top: 21px;
    left: 27em;
    z-index: 10000;

    &:hover {
      cursor: pointer;
    }

    svg {
      path {
        fill: white;
      }
    }
  }

  @media all and (min-width: 315px) and (max-width: 450px) {
    width: 100%;
    top: 0;
    left: 0;
    margin-top: 4em;
  }
`;

const Form = styled.form`
  width: 100%;
  padding: 0 1em;
`;

interface ICloseIcon {
  className?: string;
  onClick?: () => void;
  style?: any;
}

const CloseIcon: React.FC<ICloseIcon> = ({ className, onClick, style }) => {
  return (
    <StyledIcon className={className} onClick={onClick} style={style}>
      <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.7 0.3C13.3 -0.1 12.7 -0.1 12.3 0.3L7 5.6L1.7 0.3C1.3 -0.1 0.7 -0.1 0.3 0.3C-0.1 0.7 -0.1 1.3 0.3 1.7L5.6 7L0.3 12.3C-0.1 12.7 -0.1 13.3 0.3 13.7C0.5 13.9 0.7 14 1 14C1.3 14 1.5 13.9 1.7 13.7L7 8.4L12.3 13.7C12.5 13.9 12.8 14 13 14C13.2 14 13.5 13.9 13.7 13.7C14.1 13.3 14.1 12.7 13.7 12.3L8.4 7L13.7 1.7C14.1 1.3 14.1 0.7 13.7 0.3Z"
          fill="#3E3E3E"
        />
      </svg>
    </StyledIcon>
  );
};

const StyledIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 20px;
  height: 20px;

  @media all and (min-width: 315px) and (max-width: 450px) {
    margin-left: -8em;
  }
  @media all and (max-width: 315px) {
    margin-left: -11.4em;
  }
`;
